import React, { ReactNode, useState, FunctionComponent } from 'react'
import { useTable } from 'react-table'
import { TFunction } from 'next-i18next'
import cn from 'classnames'
import Octicon, { Check, X, Clock } from '@primer/octicons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { EmptyState } from '@viadelivery/design-system'
import { Button } from '@viadelivery/design-system'
import Icon from '~/utils/Icon'
import OrderDateFormatter from '../../components/OrderDateFormatter'
import AwaitOrderItems from '../../components/OrdersPage/AwaitOrderItems'
import OrderItems from '../../components/OrdersPage/OrderItems'

import { StyledTable, Td } from './styles'
import styles from '~/styles/table.module.css'
import { addCellType, dateFormatter, currencyFormatter, weightByLocaleFormatter } from '../../utils/formatters'
import { LOCALE } from '~/types'

type Column = {
  Header: string
  accessor: string
  Cell?: ReactNode
}

interface Props {
  columns: Column[]
  t: TFunction
  data?: []
  emptyState: string
  handleRowClick?: (data: any) => void
  selectedRowIndex?: number | string
  locale: LOCALE
  isLoading?: boolean
  fetchMoreData: () => void
  hasMore?: boolean
}

const LoadingIndicator = () => <div className="flex justify-center my-10">
	<Icon name='nineDots'/>
</div>;

const Table = (props) => {
	const {columns, data, isLoading, emptyState} = props;

	if (isLoading) return <LoadingIndicator/>;

	if (data == null || columns == null || (data.length > 0 && data[0] == null)) return <LoadingIndicator/>;

	return <TableInner {...props}/>
}

const TableInner: FunctionComponent<Props> = ({
  columns,
  data,
  t,
  emptyState,
  handleRowClick,
  selectedRowIndex = '',
  locale,
  isLoading,
  fetchMoreData,
  hasMore,
  order,
  clickedOrder,
  onNewOrder,
  onEditOrder,
  setOnExternalEventCallback,
  isSyntheticDealer,
  authDealerId,
  updatePoints,
  currentDealer,
  ordersWithShownItems,
  setOrdersWithShownItems,
  loading,
  fetchedOrderInfos,
  isHoursAndMinutesShown,
  onAction,
  timezoneCode,
  isOrderDetailsShown,
  datePickerColumn,
}: Props) => {
  const resultTable = useTable({
	columns: columns || [],
	data: data || [],
  });

  if (!resultTable) {
  	return (
			<EmptyState text={emptyState} />
		)
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
  } = resultTable
	let fieldForSortingBefore = null

	const onClick = (row, event) => {
	  const target = event.target as HTMLElement
	  if (!(target.nodeName === 'BUTTON' || target.closest('button'))) {
		  if (handleRowClick) {
				handleRowClick({ ...row.original, index: row.id })
		  }
		}
	}

	const getTd = (unformattedCell, i, row, isLightGrey) => {
		const cell = addCellType(unformattedCell)
	  let timezone = timezoneCode
	  if (timezoneCode === 'currentClient') {
	  	timezone = row?.original?.currentDealerTimezone
	  } else if (timezoneCode === 'clientFromOrder') {
	  	timezone = row?.original?.dealerFromOrderTimezone
	  } else if (timezoneCode === 'customer') {
	  	timezone = row?.original?.customerTimezone
	  }
	  let npsClassName = null
	  if (cell.type === 'nps' && (cell.value || cell.value === 0)) {
	  	if (cell.value >= 9) {
	  		npsClassName = styles.npsPromoters
	  	} else if (cell.value <= 6) {
	  		npsClassName = styles.npsDetractors
	  	} else {
	  		npsClassName = styles.npsPassives
	  	}
	  }
	  let tdClass = cn("px-0 py-2 text-left", {[`text-${cell?.textAlign}`]: cell?.textAlign})
	  if (unformattedCell?.column?.Header === 'Last Day for Customer Pickup') {
	  	tdClass += ' ' + styles.longExpirationHeader
	  } else if (unformattedCell?.column?.Header === 'Delivery Location') {
	  	tdClass += ' ' + styles.deliveryLocationTd
	  } else if (cell.column.id === 'ext_key') {
	  	if (row?.original?.['Origin Address Change Date']) {
	  		tdClass += ' ' + styles.originAddressChanged
	  	}
	  }
	  let coloredStatuses = ['APPROVED', 'PLACED_IN_POSTAMAT', 'PICKED_UP', 'CANCELLED', 'IN_PROGRESS']
	  	coloredStatuses.push('CREATED')
	  	coloredStatuses.push('UNCLAIMED')
	  	coloredStatuses.push('PROBLEM')
	  	coloredStatuses.push('REJECTED')
	  	coloredStatuses.push('RETURNED')

	  return (
			<Td
			  key={cell.value}
			  className={tdClass}
			  {...cell.getCellProps()}
			  status={cell.type === 'status'}
			>
					<div
						className={styles.orderListCell + (isLightGrey ? ' ' + styles.isLightGrey : '')}
					>
					  {cell.type === 'status' && (
							<span
							  // TODO: better ways to manage status colours
							  className={cn(
								'text-sm font-bold px-2 py-1 rounded text-center',
								{
									[styles.receivedByViaD]: cell.value === 'CREATED',
								  [styles.approvedStatus]: cell.value === 'APPROVED',
									[styles.inProgressStatus]: cell.value === 'IN_PROGRESS',
									[row.isCustomerSaidPickedUp ? styles.customerSaidPickedUp : styles.pickedUpStatus]: cell.value === 'PICKED_UP',
									[styles.returnedStatus]: cell.value === 'RETURNED',
								  'bg-indigo-200 text-indigo-700': cell.value === 'PLACED_IN_POSTAMAT',
								  'bg-orange-200 text-orange-700': cell.value === 'EXPIRING',
								  'bg-red-200 text-red-700': [ 'UNCLAIMED', 'PROBLEM', 'REJECTED' ].includes(cell.value),
								  'bg-gray-300 text-gray-800': cell.value === 'CANCELLED',
									'bg-gray-200 text-gray-700': !(coloredStatuses.includes(cell.value)),
								},
							  )}
							>
							  {t(`TEMPStatuses:${cell.value}`)}
							</span>
					  )}
					  {cell.type === 'true-false' && (
							<div className="text-sm font-bold px-2">
							  {cell.value ? (
								<Octicon
								  icon={Check}
								  className="text-green-400"
								/>
							  ) : (
								<Octicon icon={X} className="text-red-400" />
							  )}
							</div>
					  )}
					  {cell.type === 'timezone' ? (
					  	<span>{timezone || '0'}</span>
						) : ''}

					  {cell.type === 'normal' && cell.render('Cell')}

					  {cell.type === 'date' && cell.value ?
							<OrderDateFormatter
								date={cell.value}
								type={cell.column.id}
								picked_up={row?.original?.delivery_complete}
								expiration_date={row?.original?.expiration_date}
								row={row?.original}
							>
							  {dateFormatter(cell.value, locale, 0, 0, isHoursAndMinutesShown)}
							</OrderDateFormatter>
							: ''
					  }
					  {cell.type === 'currency' && cell.value ?
							currencyFormatter({ currency: row.original.currency, value: cell.value })
							: ''
					  }
					  {cell.type === 'weight' ?
							(locale === 'en' ? weightByLocaleFormatter({ weight: cell.value, locale }) : cell.value)
							: ''
					  }
					  {cell.type === 'delivery-tracking' && cell.value ? (() => {
							return (
							  <a className="underline text-blue-700" href={cell.value} target="_blank">{row.original.delivery_tracking}</a>
							)
					  })() : ''}
					  {cell.type === 'nps' ?
							<div
								className={npsClassName}
							>
								{cell.value}
							</div>
							: ''
					  }
					  {cell.type === 'Why Via?' ?
							<div
								className={styles.whyVia}
							>
								{t(cell.value)}
							</div>
							: ''
					  }
				</div>
			</Td>
	  )
	}

	const getOrderItems = (row) => {
		return (
			<>
				<AwaitOrderItems
					orderId={row.original.id}
					clickedOrderId={clickedOrder}
					currentOrderId={order?.id}
					loading={loading}
				/>
				<OrderItems
					order={ordersWithShownItems?.[row.original.id] ? fetchedOrderInfos?.[row.original.id] : null}
					orderId={row.original.id}
					setOnExternalEventCallback={setOnExternalEventCallback}
					clickedOrder={clickedOrder}
					onNewOrder={onNewOrder}
					onEditOrder={onEditOrder}
					updatePoints={updatePoints}
					showCloneButton={true}
					authDealerId={authDealerId}
					isInHierarchy={data.isInHierarchy}
					isHierarchyEnd={data.isHierarchyEnd}
					ordersWithShownItems={ordersWithShownItems}
          setOrdersWithShownItems={setOrdersWithShownItems}
					currentDealer={currentDealer}
					onAction={onAction}
					isOrderDetailsShown={isOrderDetailsShown}
					locale={locale}
					t={t}
				/>
				<div />
			</>
		)
	}
	const getCustomerComment = (row, hierarchySecondClassName, isSelectedRow) => {
		let customerComment = null
	  if (typeof row?.original?.customer_comment === 'string') {
	  	customerComment = row.original.customer_comment.replace(/\n/, ' ')
	  }
	  let trClassName = styles.customerComment + ' ' + hierarchySecondClassName
	  if (customerComment) {
			return (
				<tr
			  	className={trClassName}
			  >
					<td
						colSpan="77"
						className={styles.colspan100}
					>
						<div
							className={isSelectedRow ? styles.isLightGrey : null}
						>
							<div>
								<span>
									{t('Customer comment')}: "
								</span>
								<span>
									{customerComment}
								</span>
								<span>
									"
								</span>
							</div>
						</div>
					</td>
			  </tr>
			)
		} else {
			return null
		}
	}

	const uniqueOrders = {}

  return (
    <>
      {!data?.length && <EmptyState text={emptyState} />}
      {!!data?.length && (
        <InfiniteScroll
          dataLength={rows.length}
          next={() => fetchMoreData()}
          hasMore={hasMore as boolean}
          loader={
            <div className="flex justify-center my-2">
              Loading ...
            </div>
          }
        >
          <StyledTable className="w-full" {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr
                  className="text-gray-500"
                  key={headerGroup.id}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(column => {
                  	let thClass = styles.sticky
										if ([ 'Address Change Date', 'Delivery Started Date', 'Delivery Date', 'Expiration Date' ].includes(column.Header)) {
											thClass = ' ' + styles[column.Header.replace(/ /g, '_')]
										}
										let columnName = column.render('Header')
										if (columnName === 'Date / Time') {
											if (datePickerColumn === 'lastOrderUpdate') {
												columnName = 'lastOrderUpdateColumn'
											}
										}
                  	return (
	                    <th
	                      key={column.Header}
	                      className={cn("font-semibold px-4 py-2 text-sm text-left" , {[`text-${column?.textAlign}`]: column?.textAlign}) + ' ' +  (thClass || '')}
	                      {...column.getHeaderProps()}
	                    >
	                      {t(columnName)}
	                    </th>
	                  )
                  })}
                </tr>
              ))}
            </thead>
            <tbody
              className="bg-white shadow-xl text-gray-800"
              {...getTableBodyProps()}
            >
              {rows.map((row, i) => {
              	if (!uniqueOrders[row.original.dealer_name]?.[row.original.ext_key]) {
	              	if (!uniqueOrders[row.original.dealer_name]) {
	              		uniqueOrders[row.original.dealer_name] = {}
	              	}
	              	uniqueOrders[row.original.dealer_name][row.original.ext_key] = true
	                prepareRow(row)
	                let isHierarchyStart = false
	                let isHierarchyEnd = false
								  const data = row?.original
								  if (data) {
								  	const nextRow = (rows[i + 1] || {})
								  	const nextData = nextRow?.original
								  	if (fieldForSortingBefore !== data.fieldForSorting) {
								  		fieldForSortingBefore = data.fieldForSorting
								  		if (data.isInHierarchy) {
								  			isHierarchyStart = true
								  		}
								  	}
								  	if (fieldForSortingBefore !== nextData?.fieldForSorting) {
								  		isHierarchyEnd = true
								  	}
								  	if (isHierarchyStart && isHierarchyEnd) {
								  		isHierarchyStart = false
								  		isHierarchyEnd = false
								  		data.isInHierarchy = false
								  	}
								  }
								  let hierarchyFirstClassName = ''
								  let hierarchySecondClassName = ''
								  if (data.isInHierarchy) {
								  	hierarchyFirstClassName += ' ' + styles.isInHierarchy
								  	hierarchySecondClassName += ' ' + styles.isInHierarchy
								  	if (isHierarchyStart) {
								  		hierarchyFirstClassName += ' ' + styles.isHierarchyStart
								  		hierarchySecondClassName += ' ' + styles.isHierarchyStart
								  	}
								  }
								  const isSelectedRow = (selectedRowIndex === row.id)
								  const customerCommentTr = getCustomerComment(row, hierarchySecondClassName, isSelectedRow)
								  if (data.isInHierarchy) {
								  	if (isHierarchyEnd) {
								  		hierarchySecondClassName += ' ' + styles.isHierarchyEnd
								  	}
								  }
								  const isCustomerSaidPickedUp = (row?.original?.['delivery_date'] && row.original['delivery_date'] === row.original['delivery_complete'])
								  row.isCustomerSaidPickedUp = isCustomerSaidPickedUp
								  let trClass = cn('hover:bg-gray-200 cursor-pointer') + hierarchyFirstClassName
								  if (!data.isInHierarchy && selectedRowIndex === row.id) {
								  	trClass += ' ' + styles.isLightGrey
								  }
								  if (customerCommentTr) {
								  	trClass += ' ' + styles.hasCustomerComment
								  }

	                return (
										<React.Fragment key={row.original.dealer_name + row.values.ext_key} >
										  <tr
												key={row.original.dealer_name + row.values.ext_key}
												data-testid={`tr-${row?.values?.ext_key}`}
												{...row.getRowProps()}
												className={trClass}
												onClick={onClick.bind(this, row)}
										  >
												{row.cells.map((unformattedCell, i) => {
												  return getTd(unformattedCell, i, row, selectedRowIndex === row.id)
												})}
										  </tr>
										  {customerCommentTr}
										  <tr
										  	className={hierarchySecondClassName ?? ''}
										  >
												<td
													colSpan="100"
													className={styles.colspan100}
												>
													{getOrderItems(row)}
												</td>
										  </tr>
										</React.Fragment>
	                )
	              }
              })}
            </tbody>
          </StyledTable>
        </InfiniteScroll>
      )}
    </>
  )
}

export default Table
